<template>
  <div>
    <HrPageLayout
      title="My Warnings"
      subtitle="View and acknowledge your warnings"
      icon="⚠️"
      :breadcrumb-items="[
        { label: 'Dashboard', route: '/employee-dashboard' },
        { label: 'My Warnings' }
      ]"
    >
      <template #header-actions>
        <button class="hr-btn hr-btn-primary" @click="loadWarnings">
          <i class="bx bx-refresh"></i> Refresh
        </button>
      </template>

      <div class="my-warnings-content">
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-error-circle"></i></div>
            <p class="hr-metric-label">Total</p>
            <h2 class="hr-metric-value">{{ metrics.totalWarnings || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
            <p class="hr-metric-label">Active</p>
            <h2 class="hr-metric-value">{{ metrics.activeWarnings || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-check-double"></i></div>
            <p class="hr-metric-label">Pending acknowledgment</p>
            <h2 class="hr-metric-value">{{ metrics.pendingAcknowledgment || 0 }}</h2>
          </div>
        </div>

        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-body hr-py-3">
            <div class="warn-filters">
              <div class="hr-filter-group">
                <label class="hr-form-label">Status</label>
                <select v-model="filters.status" class="hr-select" @change="loadWarnings">
                  <option value="">All</option>
                  <option value="Active">Active</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Expired">Expired</option>
                </select>
              </div>
              <button class="hr-btn hr-btn-primary" @click="loadWarnings">
                <i class="bx bx-refresh"></i> Refresh
              </button>
            </div>
          </div>
        </div>

        <div v-if="loading" class="hr-loading-wrap hr-py-5">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted hr-mt-2">Loading your warnings...</p>
        </div>

        <div v-else-if="!warnings.length" class="hr-chart-card hr-text-center hr-py-5">
          <i class="bx bx-check-circle" style="font-size: 48px; color: #10b981;"></i>
          <h5 class="hr-mt-4">No warnings found</h5>
          <p class="hr-text-muted">You have no warnings on record.</p>
        </div>

        <div v-else class="hr-chart-card">
          <div class="hr-chart-body">
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Code / Type</th>
                    <th>Description</th>
                    <th>Issued</th>
                    <th>Severity</th>
                    <th>Status</th>
                    <th class="hr-text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="warning in warnings" :key="warning.id">
                    <td>
                      <strong>{{ warning.warning_code }}</strong>
                      <div class="hr-text-muted hr-small">{{ warning.warning_type }}</div>
                    </td>
                    <td>
                      <span class="warn-desc">{{ warning.description || '—' }}</span>
                      <div v-if="warning.recommended_action" class="hr-text-muted hr-small hr-mt-1">Action: {{ warning.recommended_action }}</div>
                    </td>
                    <td>{{ formatDate(warning.warning_date) }}</td>
                    <td>
                      <span class="hr-badge" :class="getSeverityBadgeClass(warning.severity)">{{ warning.severity || '—' }}</span>
                    </td>
                    <td>
                      <span class="hr-badge" :class="getStatusBadgeClass(warning.status)">{{ warning.status }}</span>
                      <span v-if="warning.acknowledgment_required && !warning.acknowledged" class="hr-badge hr-badge-warning hr-ml-1">Ack required</span>
                      <span v-else-if="warning.acknowledged" class="hr-badge hr-badge-success hr-ml-1">Acknowledged</span>
                    </td>
                    <td class="hr-text-end">
                      <button
                        v-if="warning.status === 'Active' && warning.acknowledgment_required && !warning.acknowledged"
                        @click="openAckModal(warning)"
                        class="hr-btn hr-btn-sm hr-btn-success"
                        :disabled="ackingId === warning.id"
                      >
                        <i class="bx bx-check"></i> Acknowledge
                      </button>
                      <span v-else class="hr-text-muted hr-small">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="pagination.totalPages > 1" class="hr-pagination-wrap hr-mt-4">
              <div class="hr-pagination-info">Page {{ pagination.currentPage }} of {{ pagination.totalPages }}</div>
              <nav class="hr-pagination">
                <button type="button" class="hr-page-btn" :disabled="pagination.currentPage <= 1"
                        @click="changePage(pagination.currentPage - 1)">
                  <i class="bx bx-chevron-left"></i>
                </button>
                <button v-for="page in visiblePages" :key="page" type="button" class="hr-page-btn"
                        :class="{ 'hr-page-active': pagination.currentPage === page }" @click="changePage(page)">{{ page }}</button>
                <button type="button" class="hr-page-btn" :disabled="pagination.currentPage >= pagination.totalPages"
                        @click="changePage(pagination.currentPage + 1)">
                  <i class="bx bx-chevron-right"></i>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>

    <ModalWrapper ref="ackModal" title="Acknowledge Warning" size="medium">
      <template #body>
        <p v-if="selectedWarning" class="hr-mb-2">{{ selectedWarning.warning_code }} — {{ selectedWarning.warning_type }}</p>
        <p class="hr-text-muted hr-small hr-mb-3">By acknowledging, you confirm you have read and understood this warning.</p>
        <div class="hr-filter-group">
          <label class="hr-form-label">Comment (optional)</label>
          <textarea v-model="ackResponseText" class="hr-textarea" rows="3" placeholder="Add a comment if needed"></textarea>
        </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.ackModal?.close()">Cancel</button>
        <button type="button" class="hr-btn hr-btn-success" :disabled="ackingId" @click="submitAcknowledge">
          <span v-if="ackingId" class="hr-spinner small"></span>
          <i v-else class="bx bx-check"></i> Acknowledge
        </button>
      </template>
    </ModalWrapper>
  </div>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { authUrl, execute, toast } from "@/api";

export default {
  name: "MyWarnings",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      loading: true,
      ackingId: null,
      warnings: [],
      metrics: {},
      pagination: { currentPage: 1, totalPages: 1, totalWarnings: 0, limit: 10 },
      filters: { status: "" },
      selectedWarning: null,
      ackResponseText: ""
    };
  },
  computed: {
    visiblePages() {
      const total = this.pagination.totalPages;
      if (total <= 1) return [];
      const current = this.pagination.currentPage;
      const maxVisible = 5;
      let start = Math.max(1, current - Math.floor(maxVisible / 2));
      let end = Math.min(total, start + maxVisible - 1);
      if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
      const pages = [];
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  mounted() {
    this.loadWarnings();
  },
  methods: {
    loadWarnings() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getMyWarnings");
      data.append("page", this.pagination.currentPage);
      data.append("limit", this.pagination.limit);
      if (this.filters.status) data.append("status", this.filters.status);
      execute(data, authUrl)
        .then((res) => {
          this.loading = false;
          if (res.data.success) {
            this.warnings = res.data.data.warnings || [];
            this.metrics = res.data.data.metrics || {};
            this.pagination = res.data.data.pagination || this.pagination;
          } else {
            toast.error(res.data.message || "Failed to load warnings");
            this.warnings = [];
          }
        })
        .catch((err) => {
          this.loading = false;
          toast.error(err.message || "Failed to load warnings");
          this.warnings = [];
        });
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.pagination = { ...this.pagination, currentPage: page };
      this.loadWarnings();
    },
    formatDate(val) {
      if (!val) return "—";
      const d = new Date(val);
      return isNaN(d.getTime()) ? val : d.toLocaleDateString();
    },
    getSeverityBadgeClass(severity) {
      const s = (severity || "").toLowerCase();
      if (s === "high") return "hr-badge-danger";
      if (s === "medium") return "hr-badge-warning";
      if (s === "low") return "hr-badge-info";
      return "hr-badge-secondary";
    },
    getStatusBadgeClass(status) {
      const s = (status || "").toLowerCase();
      if (s === "active") return "hr-badge-warning";
      if (s === "resolved") return "hr-badge-success";
      if (s === "expired") return "hr-badge-secondary";
      return "hr-badge-secondary";
    },
    openAckModal(warning) {
      this.selectedWarning = warning;
      this.ackResponseText = "";
      this.$refs.ackModal?.open();
    },
    submitAcknowledge() {
      if (!this.selectedWarning) return;
      this.ackingId = this.selectedWarning.id;
      const data = new FormData();
      data.append("function", "acknowledgeWarning");
      data.append("warningId", this.selectedWarning.id);
      data.append("responseText", this.ackResponseText);
      execute(data, authUrl)
        .then((res) => {
          this.ackingId = null;
          this.$refs.ackModal?.close();
          this.selectedWarning = null;
          this.ackResponseText = "";
          if (res.data.success) {
            toast.success("Warning acknowledged");
            this.loadWarnings();
          } else {
            toast.error(res.data.message || "Failed to acknowledge");
          }
        })
        .catch((err) => {
          this.ackingId = null;
          toast.error(err.message || "Failed to acknowledge");
        });
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.warn-filters { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.warn-desc { word-break: break-word; }
.hr-text-end { text-align: right; }
.hr-ml-1 { margin-left: 6px; }
</style>
